import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { 
  Briefcase, MapPin, Calendar, Clock, Phone, User, Loader2, 
  CheckCircle, CheckCircle2, XCircle, ChevronRight, PhoneCall, X, Wrench,
  Play, ArrowRight, ThumbsUp, ThumbsDown, Plus, Trash2, 
  DollarSign, FileText, Send, Package, Search, FileCheck, Receipt, Paperclip, Camera,
  CreditCard, Banknote, Smartphone, List as ListIcon, Map as MapIcon
} from "lucide-react";
import TechJobMap from "./TechJobMap";
import { toast } from "sonner";
import { formatDateWithDay, formatJobScheduleDisplay } from "../../utils/dateUtils";
import JobAttachments from "../../components/jobs/JobAttachments";
import { useTechPermissions, allowed, enabled } from "../../contexts/TechPermissionsContext";
import { getDisplayAddress, getGoogleMapsUrl } from "../../utils/job-form-utils";
import { getStatusConfig, getStatusCategory, getDynamicStatuses, isFinishedStatus } from "../../components/jobs/jobCardUtils";
import { fetchJobStatuses } from "../../hooks/useJobStatuses";
import { ItemAutocomplete } from "../../components/invoices/ItemsLibrary";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";
import { Tag, Navigation, ExternalLink } from "lucide-react";
import { SendForSignatureModal, SignatureStatusBadge } from "../../components/tech/SendForSignatureModal";
import { TechCollectPaymentModal } from "../../components/tech/TechCollectPaymentModal";
import { PenLine, ShieldOff, Mail, Pencil, Copy } from "lucide-react";
import { copyTextAsync } from "../../utils/clipboard";
import { useTheme } from "../../contexts/ThemeContext";

// ==================== Permission-gated job status menu ====================
const TECH_STATUS_OPTIONS = [
  { value: "estimate", label: "Mark as Estimate", icon: FileCheck, color: "#F97316" },
  { value: "deposit_collected", label: "Deposit Collected", icon: DollarSign, color: "#14B8A6" },
  { value: "cancelled", label: "Cancel Job", icon: XCircle, color: "#EF4444" },
];

const JobStatusMenu = ({ job, updating, onSet, extraOptions = [] }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        size="sm"
        disabled={updating}
        className="text-xs h-8 rounded-lg border bg-purple-500/10 hover:bg-purple-500/20 text-purple-500 border-purple-500/20"
        data-testid={`set-status-btn-${job.job_id}`}
      >
        {updating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5 sm:mr-1" />}
        <span className="hidden sm:inline">Status</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-48">
      {[...TECH_STATUS_OPTIONS, ...extraOptions].filter((o) => o.value !== job.status).map((o) => (
        <DropdownMenuItem
          key={o.value}
          onClick={() => onSet(job.job_id, o.value)}
          data-testid={`status-option-${o.value}-${job.job_id}`}
          className="gap-2 text-sm cursor-pointer"
        >
          <o.icon className="w-4 h-4" style={{ color: o.color }} />
          {o.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// ==================== Status Change Note Modal ====================
const STATUS_NOTE_META = {
  estimate: { label: "Mark as Estimate", icon: FileCheck, color: "#F97316" },
  deposit_collected: { label: "Deposit Collected", icon: DollarSign, color: "#14B8A6" },
  cancelled: { label: "Cancel Job", icon: XCircle, color: "#EF4444" },
};

const StatusNoteModal = ({ job, status, onClose, onSubmit, submitting }) => {
  const [note, setNote] = useState("");
  const [priceGiven, setPriceGiven] = useState("");
  const meta = STATUS_NOTE_META[status];
  const noteRequired = status === "cancelled";
  const Icon = meta.icon;

  const handleSubmit = () => {
    if (noteRequired && !note.trim()) { toast.error("Please add a note explaining why the job is cancelled"); return; }
    onSubmit({
      status,
      status_note: note.trim() || undefined,
      price_given: priceGiven !== "" ? parseFloat(priceGiven) : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={onClose} data-testid="status-note-modal">
      <div className="relative rounded-2xl max-w-md w-full bg-background border border-border shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="p-4 flex items-center justify-between border-b border-border">
          <div className="flex items-center gap-2.5">
            <Icon className="w-5 h-5" style={{ color: meta.color }} />
            <div>
              <h2 className="text-lg font-bold text-foreground font-['Manrope']">{meta.label}</h2>
              <p className="text-muted-foreground/60 text-xs mt-0.5">{job?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="close-status-note-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5" /> Note {noteRequired ? <span className="text-red-500">*</span> : <span className="text-muted-foreground/50 normal-case">(optional)</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={status === "cancelled" ? "Why was this job cancelled?" : "Reason for the status change..."}
              className="w-full px-3 py-2 text-sm rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/40 transition-colors resize-none"
              data-testid="status-note-input"
            />
            <p className="text-[11px] text-muted-foreground/50 mt-1">This will be added to the job notes for the office.</p>
          </div>

          <div>
            <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <DollarSign className="w-3.5 h-3.5 text-emerald-500" /> Price Given <span className="text-muted-foreground/50 normal-case">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 text-sm">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={priceGiven}
                onChange={(e) => setPriceGiven(e.target.value)}
                placeholder="0.00"
                className="w-full h-9 pl-7 pr-3 text-sm rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/40 transition-colors"
                data-testid="status-price-given-input"
              />
            </div>
            <p className="text-[11px] text-muted-foreground/50 mt-1">Final amount quoted to the customer.</p>
          </div>
        </div>

        <div className="p-4 flex items-center justify-end gap-2 border-t border-border">
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground rounded-xl" data-testid="cancel-status-note">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-xl text-white"
            style={{ backgroundColor: meta.color }}
            data-testid="confirm-status-note-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Icon className="w-4 h-4 mr-1.5" />}
            {meta.label}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== Inline Customer Email Editor (permission-gated) ====================
const CustomerEmailEditor = ({ job, canAdd, canEdit, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(job.customer_email || "");
  const [saving, setSaving] = useState(false);
  const hasEmail = !!job.customer_email;
  const mayEdit = hasEmail ? canEdit : (canAdd || canEdit);

  const save = async () => {
    const email = value.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Enter a valid email address"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/tech/jobs/${job.job_id}/customer-email`, { email }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("tech_token")}` },
      });
      toast.success(hasEmail ? "Customer email updated" : "Customer email added");
      setEditing(false);
      onSaved(email);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save email");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2" data-testid="customer-email-editor">
        <input
          type="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="customer@email.com"
          className="flex-1 min-w-0 h-8 px-2.5 text-xs rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/40"
          data-testid="customer-email-input-inline"
        />
        <Button size="sm" onClick={save} disabled={saving} className="h-8 text-xs rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20" data-testid="save-customer-email-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
        </Button>
        <button onClick={() => { setEditing(false); setValue(job.customer_email || ""); }} className="text-muted-foreground hover:text-foreground" data-testid="cancel-customer-email-btn">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="customer-email-row">
      {hasEmail ? (
        <>
          <span className="text-muted-foreground/60 text-xs flex items-center gap-1.5"><Mail className="w-3 h-3" /> {job.customer_email}</span>
          {mayEdit && (
            <button onClick={() => setEditing(true)} className="text-[#FFCC00]/70 hover:text-[#FFCC00] text-[11px] flex items-center gap-1" data-testid="edit-customer-email-btn">
              <Pencil className="w-3 h-3" /> Edit
            </button>
          )}
        </>
      ) : mayEdit ? (
        <button onClick={() => setEditing(true)} className="text-[#FFCC00]/70 hover:text-[#FFCC00] text-[11px] flex items-center gap-1" data-testid="add-customer-email-btn">
          <Plus className="w-3 h-3" /> Add customer email
        </button>
      ) : null}
    </div>
  );
};


const API = process.env.REACT_APP_BACKEND_URL;
// ==================== Completion Modal ====================
const CompletionModal = ({ job, onClose, onSubmit, submitting }) => {
  const [totalPrice, setTotalPrice] = useState(job?.total_price || "");
  const [parts, setParts] = useState(job?.parts?.length ? job.parts : []);
  const [payments, setPayments] = useState([{ payment_type_id: "", method: "cash", amount: "" }]);
  const [notes, setNotes] = useState(job?.notes || "");
  const [partSuggestions, setPartSuggestions] = useState({});
  const partDebounceRef = useRef({});
  const [paymentTypes, setPaymentTypes] = useState([]);

  useEffect(() => {
    const fetchPaymentTypes = async () => {
      try {
        const token = localStorage.getItem("tech_token");
        const res = await axios.get(`${API}/api/settings/payment-types`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const active = (res.data || []).filter(pt => pt.is_active !== false);
        setPaymentTypes(active);
        // Pre-select first payment type if available
        if (active.length > 0) {
          setPayments([{ payment_type_id: active[0].payment_id, method: active[0].name, amount: "" }]);
        }
      } catch { /* fallback to hardcoded options */ }
    };
    fetchPaymentTypes();
  }, []);

  const addPart = () => setParts([...parts, { name: "", cost: "", quantity: 1, provider: "company" }]);
  const removePart = (i) => {
    setParts(parts.filter((_, idx) => idx !== i));
    setPartSuggestions(prev => { const n = { ...prev }; delete n[i]; return n; });
  };
  const updatePart = (i, field, val) => {
    const updated = [...parts];
    updated[i] = { ...updated[i], [field]: val };
    setParts(updated);
  };

  const searchInventory = (index, query) => {
    if (partDebounceRef.current[index]) clearTimeout(partDebounceRef.current[index]);
    if (!query || query.length < 2) {
      setPartSuggestions(prev => ({ ...prev, [index]: [] }));
      return;
    }
    partDebounceRef.current[index] = setTimeout(async () => {
      try {
        const token = localStorage.getItem("tech_token");
        const res = await axios.get(`${API}/api/inventory/items/search?q=${encodeURIComponent(query)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPartSuggestions(prev => ({ ...prev, [index]: res.data || [] }));
      } catch {
        setPartSuggestions(prev => ({ ...prev, [index]: [] }));
      }
    }, 250);
  };

  const selectInventoryItem = (index, item) => {
    const updated = [...parts];
    updated[index] = { ...updated[index], name: item.name, cost: item.unit_price, item_id: item.item_id };
    setParts(updated);
    setPartSuggestions(prev => ({ ...prev, [index]: [] }));
  };

  const addPayment = () => {
    const defaultPt = paymentTypes.length > 0 ? paymentTypes[0] : null;
    setPayments([...payments, {
      payment_type_id: defaultPt?.payment_id || "",
      method: defaultPt?.name || "cash",
      amount: ""
    }]);
  };
  const removePayment = (i) => setPayments(payments.filter((_, idx) => idx !== i));
  const updatePayment = (i, field, val) => {
    const updated = [...payments];
    if (field === "payment_type_id") {
      const pt = paymentTypes.find(p => p.payment_id === val);
      updated[i] = { ...updated[i], payment_type_id: val, method: pt?.name || val };
    } else {
      updated[i] = { ...updated[i], [field]: val };
    }
    setPayments(updated);
  };

  const partsTotal = parts.reduce((sum, p) => sum + (parseFloat(p.cost || 0) * parseInt(p.quantity || 1)), 0);

  const handleSubmit = () => {
    const data = {
      status: "completed",
      total_price: parseFloat(totalPrice) || 0,
      parts: parts.filter(p => p.name).map(p => ({
        name: p.name,
        cost: parseFloat(p.cost) || 0,
        quantity: parseInt(p.quantity) || 1,
        provider: p.provider || "company"
      })),
      payments: payments.filter(p => p.amount).map(p => ({
        payment_type_id: p.payment_type_id || "",
        method: p.method,
        amount: parseFloat(p.amount) || 0
      })),
      notes: notes || undefined,
    };
    onSubmit(data);
  };

  const inputCls = "w-full h-9 px-3 text-sm rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/40 transition-colors";
  const selectCls = "h-9 px-2 text-sm rounded-lg bg-muted border border-border text-foreground focus:outline-none focus:border-primary/40 transition-colors appearance-none";

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={onClose} data-testid="completion-modal">
      <div className="relative rounded-2xl max-w-lg w-full max-h-[90vh] overflow-auto bg-background border border-border shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 p-4 flex items-center justify-between bg-background border-b border-border">
          <div>
            <h2 className="text-lg font-bold text-foreground font-['Manrope']">Complete Job</h2>
            <p className="text-muted-foreground/60 text-xs mt-0.5">{job?.title}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="close-completion-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-5">
          {/* Total Price */}
          <div>
            <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 flex items-center gap-2">
              <DollarSign className="w-3.5 h-3.5 text-emerald-500" /> Total Price
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 text-sm">$</span>
              <input
                type="number"
                step="0.01"
                value={totalPrice}
                onChange={(e) => setTotalPrice(e.target.value)}
                placeholder="0.00"
                className={`${inputCls} pl-7`}
                data-testid="completion-total-price"
              />
            </div>
          </div>

          {/* Payments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider flex items-center gap-2">
                <DollarSign className="w-3.5 h-3.5 text-blue-400" /> Payments
              </label>
              <button onClick={addPayment} className="text-[#FFCC00]/70 hover:text-[#FFCC00] text-xs flex items-center gap-1" data-testid="add-payment-btn">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="space-y-2">
              {payments.map((pay, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`payment-row-${i}`}>
                  <select
                    value={pay.payment_type_id || pay.method}
                    onChange={(e) => updatePayment(i, paymentTypes.length > 0 ? "payment_type_id" : "method", e.target.value)}
                    className={`${selectCls} w-[120px]`}
                    data-testid={`payment-method-${i}`}
                  >
                    {paymentTypes.length > 0 ? (
                      paymentTypes.map(pt => (
                        <option key={pt.payment_id} value={pt.payment_id}>{pt.name}</option>
                      ))
                    ) : (
                      <>
                        <option value="cash">Cash</option>
                        <option value="check">Check</option>
                        <option value="credit_card">Credit Card</option>
                        <option value="zelle">Zelle</option>
                        <option value="venmo">Venmo</option>
                        <option value="other">Other</option>
                      </>
                    )}
                  </select>
                  <div className="relative flex-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 text-sm">$</span>
                    <input
                      type="number"
                      step="0.01"
                      value={pay.amount}
                      onChange={(e) => updatePayment(i, "amount", e.target.value)}
                      placeholder="0.00"
                      className={`${inputCls} pl-7`}
                      data-testid={`payment-amount-${i}`}
                    />
                  </div>
                  {payments.length > 1 && (
                    <button onClick={() => removePayment(i)} className="text-red-400/50 hover:text-red-400 p-1" data-testid={`remove-payment-${i}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Parts */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-orange-400" /> Parts
              </label>
              <button onClick={addPart} className="text-[#FFCC00]/70 hover:text-[#FFCC00] text-xs flex items-center gap-1" data-testid="add-part-btn">
                <Plus className="w-3 h-3" /> Add Part
              </button>
            </div>
            {parts.length === 0 ? (
              <p className="text-muted-foreground/30 text-xs text-center py-3">No parts added</p>
            ) : (
              <div className="space-y-2">
                {parts.map((part, i) => (
                  <div key={i} className="rounded-lg p-3 space-y-2 bg-muted/30 border border-border/50" data-testid={`part-row-${i}`}>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/40 pointer-events-none" />
                        <input
                          type="text"
                          value={part.name}
                          onChange={(e) => { updatePart(i, "name", e.target.value); searchInventory(i, e.target.value); }}
                          onBlur={() => setTimeout(() => setPartSuggestions(prev => ({ ...prev, [i]: [] })), 200)}
                          placeholder="Search inventory..."
                          className={`${inputCls} flex-1 pl-9`}
                          data-testid={`part-name-${i}`}
                        />
                        {partSuggestions[i]?.length > 0 && (
                          <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-background border border-border rounded-lg shadow-xl max-h-40 overflow-y-auto overscroll-contain touch-pan-y" data-testid={`part-suggestions-${i}`}>
                            {partSuggestions[i].map((item) => (
                              <button
                                key={item.item_id}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => selectInventoryItem(i, item)}
                                className="w-full text-left px-3 py-2 hover:bg-muted/60 flex items-center justify-between gap-2 text-sm transition-colors"
                              >
                                <div className="min-w-0">
                                  <span className="font-medium text-foreground block truncate">{item.name}</span>
                                  <span className="text-xs text-muted-foreground">{item.sku}{item.category ? ` · ${item.category}` : ""}</span>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <span className="text-xs font-semibold text-foreground">${item.unit_price}</span>
                                  <span className="text-[10px] text-muted-foreground block">{item.stock_quantity} in stock</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button onClick={() => removePart(i)} className="text-red-400/50 hover:text-red-400 p-1" data-testid={`remove-part-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/40 text-sm">$</span>
                        <input
                          type="number"
                          step="0.01"
                          value={part.cost}
                          onChange={(e) => updatePart(i, "cost", e.target.value)}
                          placeholder="Cost"
                          className={`${inputCls} pl-7`}
                          data-testid={`part-cost-${i}`}
                        />
                      </div>
                      <input
                        type="number"
                        value={part.quantity}
                        onChange={(e) => updatePart(i, "quantity", e.target.value)}
                        min="1"
                        className={`${inputCls} w-16 text-center`}
                        placeholder="Qty"
                        data-testid={`part-qty-${i}`}
                      />
                      <select
                        value={part.provider}
                        onChange={(e) => updatePart(i, "provider", e.target.value)}
                        className={`${selectCls} w-[110px]`}
                        data-testid={`part-provider-${i}`}
                      >
                        <option value="company">Company</option>
                        <option value="technician">Technician</option>
                      </select>
                    </div>
                  </div>
                ))}
                {partsTotal > 0 && (
                  <div className="flex justify-end text-xs text-muted-foreground/50 pt-1">
                    Parts total: <span className="text-foreground ml-1">${partsTotal.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 block">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              rows={2}
              className={`${inputCls} h-auto py-2 resize-none`}
              data-testid="completion-notes"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 p-4 flex gap-2 justify-end bg-background border-t border-border">
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground rounded-xl" data-testid="cancel-completion">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-400 border border-emerald-500/25 rounded-xl"
            data-testid="submit-completion"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Complete Job
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== Send Invoice Modal ====================
const SendInvoiceModal = ({ job, onClose, onSubmit, submitting }) => {
  const [items, setItems] = useState(() => {
    const initial = [];
    if (job?.total_price) {
      const partsTotal = (job.parts || []).reduce((s, p) => s + (parseFloat(p.cost || 0) * parseInt(p.quantity || 1)), 0);
      const labor = (job.total_price || 0) - partsTotal;
      if (labor > 0) initial.push({ description: "Labor", quantity: 1, unit_price: labor.toFixed(2) });
      (job.parts || []).forEach(p => {
        initial.push({ description: p.name, quantity: p.quantity || 1, unit_price: parseFloat(p.cost || 0).toFixed(2) });
      });
    }
    return initial.length ? initial : [{ description: "", quantity: 1, unit_price: "" }];
  });
  const [customerEmail, setCustomerEmail] = useState(job?.customer_email || "");
  const [notes, setNotes] = useState("");

  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: val };
    setItems(updated);
  };

  const subtotal = items.reduce((s, item) => s + (parseFloat(item.unit_price || 0) * parseInt(item.quantity || 1)), 0);

  const handleSubmit = () => {
    const validItems = items.filter(i => i.description && i.unit_price);
    if (!validItems.length) { toast.error("Add at least one item"); return; }
    onSubmit({
      job_id: job.job_id,
      items: validItems.map(i => ({ description: i.description, quantity: parseInt(i.quantity) || 1, unit_price: parseFloat(i.unit_price) || 0 })),
      subtotal,
      tax_rate: 0,
      tax_amount: 0,
      total: subtotal,
      notes: notes || undefined,
      customer_email: customerEmail || undefined,
    });
  };

  const inputCls = "w-full h-9 px-3 text-sm rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/40 transition-colors";

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={onClose} data-testid="invoice-modal">
      <div className="relative rounded-2xl max-w-lg w-full max-h-[90vh] overflow-auto bg-background border border-border shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 p-4 flex items-center justify-between bg-background border-b border-border">
          <div>
            <h2 className="text-lg font-bold text-foreground font-['Manrope']">Send Invoice</h2>
            <p className="text-muted-foreground/60 text-xs mt-0.5">{job?.title} - {job?.customer_name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="close-invoice-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Customer email */}
          <div>
            <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 block">Customer Email</label>
            <input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="customer@email.com"
              className={inputCls}
              data-testid="invoice-customer-email"
            />
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-blue-400" /> Line Items
              </label>
              <button onClick={addItem} className="text-[#FFCC00]/70 hover:text-[#FFCC00] text-xs flex items-center gap-1" data-testid="add-invoice-item">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="space-y-3">
              {items.map((item, i) => (
                <div
                  key={i}
                  className="rounded-lg bg-muted/50 border border-border/50 p-2.5 space-y-2"
                  data-testid={`invoice-item-${i}`}
                >
                  <input
                    type="text"
                    value={item.description}
                    onChange={(e) => updateItem(i, "description", e.target.value)}
                    placeholder="Item or service description"
                    className={`${inputCls} w-full`}
                    data-testid={`invoice-desc-${i}`}
                  />
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 shrink-0">Qty</span>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(i, "quantity", e.target.value)}
                        min="1"
                        className={`${inputCls} w-full text-center`}
                        data-testid={`invoice-qty-${i}`}
                      />
                    </div>
                    <div className="flex-1 relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/40 text-sm">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                        placeholder="0.00"
                        className={`${inputCls} pl-6 w-full`}
                        data-testid={`invoice-price-${i}`}
                      />
                    </div>
                    <div className="text-right text-xs font-medium text-foreground tabular-nums w-16 shrink-0">
                      ${(parseFloat(item.unit_price || 0) * parseInt(item.quantity || 1)).toFixed(2)}
                    </div>
                    {items.length > 1 && (
                      <button
                        onClick={() => removeItem(i)}
                        className="text-red-400/60 hover:text-red-400 p-1.5 rounded hover:bg-red-500/10 shrink-0"
                        data-testid={`invoice-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div className="rounded-lg p-3 flex justify-between items-center bg-emerald-50/50 dark:bg-emerald-500/[0.04] border border-emerald-200/50 dark:border-emerald-500/12">
            <span className="text-muted-foreground text-sm font-medium">Total</span>
            <span className="text-emerald-400 text-lg font-bold" data-testid="invoice-total">${subtotal.toFixed(2)}</span>
          </div>

          {/* Notes */}
          <div>
            <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 block">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Payment terms, additional info..."
              rows={2}
              className={`${inputCls} h-auto py-2 resize-none`}
              data-testid="invoice-notes"
            />
          </div>
        </div>

        <div className="sticky bottom-0 p-4 flex gap-2 justify-end bg-background border-t border-border">
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground rounded-xl">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-blue-500/15 hover:bg-blue-500/25 text-blue-400 border border-blue-500/25 rounded-xl"
            data-testid="submit-invoice"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            {customerEmail ? "Send Invoice" : "Create Invoice"}
          </Button>
        </div>
      </div>
    </div>
  );
};


// ==================== Tech Document Form Modal (Estimate / Invoice) ====================
const TechDocFormModal = ({ job, documentType, onClose, onSubmit, submitting }) => {
  const { isDark } = useTheme();
  const isEstimate = documentType === "estimate";
  const titleLabel = isEstimate ? "Create Estimate" : "Create Invoice";
  const submitLabel = isEstimate ? "Create Estimate" : "Create Invoice";
  const accentBg = isEstimate ? "bg-orange-500/15 hover:bg-orange-500/25 text-orange-500 border-orange-500/25" : "bg-blue-500/15 hover:bg-blue-500/25 text-blue-500 border-blue-500/25";
  const totalColor = isEstimate ? "text-orange-500" : "text-emerald-500";
  const totalBorder = isEstimate ? "bg-orange-50/50 dark:bg-orange-500/[0.04] border-orange-200/50 dark:border-orange-500/12" : "bg-emerald-50/50 dark:bg-emerald-500/[0.04] border-emerald-200/50 dark:border-emerald-500/12";

  // Pre-fill items from job parts/total when present
  const [items, setItems] = useState(() => {
    const initial = [];
    if (!isEstimate && job?.total_price) {
      const partsTotal = (job.parts || []).reduce((s, p) => s + (parseFloat(p.cost || 0) * parseInt(p.quantity || 1)), 0);
      const labor = (job.total_price || 0) - partsTotal;
      if (labor > 0) initial.push({ description: "Labor", quantity: 1, unit_price: labor.toFixed(2), discount: 0 });
      (job.parts || []).forEach(p => {
        initial.push({ description: p.name, quantity: p.quantity || 1, unit_price: parseFloat(p.cost || 0).toFixed(2), discount: 0 });
      });
    }
    return initial.length ? initial : [{ description: "", quantity: 1, unit_price: "", discount: 0 }];
  });
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [paymentTerms, setPaymentTerms] = useState(isEstimate ? "" : "Due on receipt");
  const [validUntil, setValidUntil] = useState("");

  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: "", discount: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const updated = [...items];
    updated[i] = { ...updated[i], [field]: val };
    setItems(updated);
  };

  const subtotal = items.reduce((s, item) => {
    const qty = parseFloat(item.quantity || 0);
    const price = parseFloat(item.unit_price || 0);
    const disc = parseFloat(item.discount || 0);
    return s + qty * price * (1 - disc / 100);
  }, 0);
  const discountTotal = items.reduce((s, item) => {
    const qty = parseFloat(item.quantity || 0);
    const price = parseFloat(item.unit_price || 0);
    const disc = parseFloat(item.discount || 0);
    return s + qty * price * (disc / 100);
  }, 0);
  const taxAmount = subtotal * (parseFloat(taxRate) || 0) / 100;
  const total = subtotal + taxAmount;

  const handleSubmit = () => {
    const validItems = items
      .filter(i => (i.description || "").trim() && parseFloat(i.unit_price || 0) > 0)
      .map(i => ({
        description: i.description,
        quantity: parseInt(i.quantity) || 1,
        unit_price: parseFloat(i.unit_price) || 0,
        discount: parseFloat(i.discount) || 0,
      }));
    if (!validItems.length) { toast.error("Add at least one valid item"); return; }
    if (total <= 0) { toast.error("Total must be greater than 0"); return; }

    onSubmit({
      job_id: job.job_id,
      document_type: documentType,
      items: validItems,
      subtotal: Math.round(subtotal * 100) / 100,
      discount_total: Math.round(discountTotal * 100) / 100,
      tax_rate: parseFloat(taxRate) || 0,
      tax_amount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100,
      notes: notes || undefined,
      payment_terms: !isEstimate ? (paymentTerms || undefined) : undefined,
      valid_until: isEstimate ? (validUntil || undefined) : undefined,
    });
  };

  const inputCls = "w-full h-9 px-3 text-sm rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/40 transition-colors";

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-3" onClick={onClose} data-testid="tech-doc-modal">
      <div className="relative rounded-2xl max-w-lg w-full max-h-[90vh] overflow-auto bg-background border border-border shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 p-4 flex items-center justify-between bg-background border-b border-border">
          <div>
            <h2 className="text-lg font-bold text-foreground font-['Manrope']" data-testid="tech-doc-title">{titleLabel}</h2>
            <p className="text-muted-foreground/60 text-xs mt-0.5">{job?.title} - {job?.customer_name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1" data-testid="close-tech-doc-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 text-blue-400" /> Line Items
              </label>
              <button onClick={addItem} className="text-[#FFCC00]/70 hover:text-[#FFCC00] text-xs flex items-center gap-1" data-testid="add-doc-item">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
            <div className="space-y-3">
              {items.map((item, i) => (
                <div
                  key={i}
                  className="rounded-lg bg-muted/50 border border-border/50 p-2.5 space-y-2"
                  data-testid={`doc-item-${i}`}
                >
                  <ItemAutocomplete
                    value={item.description}
                    onChange={(v) => updateItem(i, "description", v)}
                    onSelect={(saved) => {
                      const updated = [...items];
                      updated[i] = {
                        ...updated[i],
                        description: saved.name,
                        unit_price: (saved.unit_price || 0).toFixed(2),
                        quantity: saved.default_quantity || updated[i].quantity || 1,
                      };
                      setItems(updated);
                    }}
                    isDark={isDark}
                    endpoint={`${API}/api/tech/saved-items`}
                    headers={{ Authorization: `Bearer ${localStorage.getItem("tech_token")}` }}
                    placeholder="Item or service description"
                    inputClassName={`${inputCls} w-full`}
                    testId={`doc-desc-${i}`}
                  />
                  <div className="flex items-center gap-2">
                    <div className="flex-1 flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 shrink-0">Qty</span>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(i, "quantity", e.target.value)}
                        min="1"
                        className={`${inputCls} w-full text-center`}
                        data-testid={`doc-qty-${i}`}
                      />
                    </div>
                    <div className="flex-1 relative">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/40 text-sm">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                        placeholder="0.00"
                        className={`${inputCls} pl-6 w-full`}
                        data-testid={`doc-price-${i}`}
                      />
                    </div>
                    <div className="text-right text-xs font-medium text-foreground tabular-nums w-16 shrink-0">
                      ${(parseFloat(item.unit_price || 0) * parseInt(item.quantity || 1)).toFixed(2)}
                    </div>
                    {items.length > 1 && (
                      <button
                        onClick={() => removeItem(i)}
                        className="text-red-400/60 hover:text-red-400 p-1.5 rounded hover:bg-red-500/10 shrink-0"
                        data-testid={`doc-remove-${i}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tax + (Valid Until / Payment Terms) */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 block">Tax Rate (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="0"
                className={inputCls}
                data-testid="doc-tax-rate"
              />
            </div>
            {isEstimate ? (
              <div>
                <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 block">Valid Until</label>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  className={inputCls}
                  data-testid="doc-valid-until"
                />
              </div>
            ) : (
              <div>
                <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 block">Payment Terms</label>
                <input
                  type="text"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  placeholder="Due on receipt"
                  className={inputCls}
                  data-testid="doc-payment-terms"
                />
              </div>
            )}
          </div>

          {/* Totals */}
          <div className={`rounded-lg p-3 space-y-1.5 border ${totalBorder}`}>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Subtotal</span>
              <span data-testid="doc-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            {discountTotal > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Discount</span>
                <span>-${discountTotal.toFixed(2)}</span>
              </div>
            )}
            {taxAmount > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Tax ({taxRate}%)</span>
                <span>${taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-1.5 border-t border-border/40">
              <span className="text-muted-foreground text-sm font-medium">Total</span>
              <span className={`${totalColor} text-lg font-bold`} data-testid="doc-total">${total.toFixed(2)}</span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-muted-foreground text-xs font-medium uppercase tracking-wider mb-2 block">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={isEstimate ? "Quote details, exclusions..." : "Payment instructions, additional info..."}
              rows={2}
              className={`${inputCls} h-auto py-2 resize-none`}
              data-testid="doc-notes"
            />
          </div>
        </div>

        <div className="sticky bottom-0 p-4 flex gap-2 justify-end bg-background border-t border-border">
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground rounded-xl">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className={`${accentBg} border rounded-xl`}
            data-testid="submit-tech-doc"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};


const SWIPE_THRESHOLD = 80;

const SwipeableJobCard = ({ children, action, onAction, disabled }) => {
  const containerRef = useRef(null);
  const startX = useRef(0);
  const currentX = useRef(0);
  const isDragging = useRef(false);
  const [offset, setOffset] = useState(0);
  const [swiping, setSwiping] = useState(false);

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;
    startX.current = e.touches[0].clientX;
    currentX.current = 0;
    isDragging.current = true;
    setSwiping(true);
  }, [disabled]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging.current) return;
    const diff = e.touches[0].clientX - startX.current;
    // Only allow right swipe, cap at 140px
    const clamped = Math.max(0, Math.min(diff, 140));
    currentX.current = clamped;
    setOffset(clamped);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (currentX.current >= SWIPE_THRESHOLD) {
      onAction?.();
    }
    setOffset(0);
    setSwiping(false);
  }, [onAction]);

  if (!action) return <div>{children}</div>;

  const progress = Math.min(offset / SWIPE_THRESHOLD, 1);
  const actionBg = action.color || "#22C55E";

  return (
    <div className="relative overflow-hidden rounded-2xl" ref={containerRef} data-testid={`swipeable-${action.testId}`}>
      {/* Background action revealed on swipe */}
      <div
        className="absolute inset-0 flex items-center rounded-2xl transition-opacity"
        style={{
          background: actionBg,
          opacity: progress * 0.95 + 0.05,
        }}
      >
        <div className="flex items-center gap-2 pl-5 text-white" style={{ opacity: progress }}>
          {action.icon}
          <span className="text-sm font-semibold whitespace-nowrap">{action.label}</span>
        </div>
      </div>
      {/* Foreground card */}
      <div
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${offset}px)`,
          transition: swiping ? "none" : "transform 0.3s cubic-bezier(.4,0,.2,1)",
        }}
        className="relative z-10"
      >
        {children}
      </div>
    </div>
  );
};

// ==================== Payment Method Picker ====================
// Lightweight bottom-sheet picker for technicians marking an invoice paid.
// Common methods kept in one tap of the thumb on a phone.
const PAYMENT_METHODS = [
  { value: "Cash", icon: Banknote, color: "text-emerald-500" },
  { value: "Credit Card", icon: CreditCard, color: "text-blue-500" },
  { value: "Check", icon: FileText, color: "text-violet-500" },
  { value: "Zelle", icon: Smartphone, color: "text-pink-500" },
  { value: "Venmo", icon: Smartphone, color: "text-blue-400" },
  { value: "Other", icon: DollarSign, color: "text-muted-foreground" },
];

const PaymentMethodPicker = ({ doc, onClose, onConfirm, submitting }) => {
  const [method, setMethod] = useState("Cash");
  return (
    <div
      className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-[60] p-0 sm:p-3"
      onClick={onClose}
      data-testid="payment-picker-modal"
    >
      <div
        className="relative rounded-t-2xl sm:rounded-2xl max-w-md w-full bg-background border-t sm:border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-foreground font-['Manrope']">Mark as paid</h2>
            <p className="text-muted-foreground/60 text-xs mt-0.5">
              Invoice #{doc?.invoice_number || doc?.invoice_id?.slice(0, 8)} · ${(doc?.total || 0).toFixed(2)}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground/70">
            How was it paid?
          </p>
          <div className="grid grid-cols-2 gap-2">
            {PAYMENT_METHODS.map(({ value, icon: Icon, color }) => (
              <button
                key={value}
                onClick={() => setMethod(value)}
                className={`flex items-center gap-2 p-3 rounded-xl border transition-colors ${
                  method === value
                    ? "border-emerald-500/40 bg-emerald-500/10"
                    : "border-border bg-muted/30 hover:bg-muted/50"
                }`}
                data-testid={`payment-method-${value.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className={`w-4 h-4 ${method === value ? "text-emerald-500" : color}`} />
                <span className={`text-sm font-medium ${method === value ? "text-emerald-500" : "text-foreground"}`}>
                  {value}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="p-4 flex gap-2 justify-end bg-background border-t border-border">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            className="text-muted-foreground hover:text-foreground rounded-xl"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(method)}
            disabled={submitting}
            className="bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-500 border border-emerald-500/25 rounded-xl"
            data-testid="confirm-mark-paid"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Mark Paid
          </Button>
        </div>
      </div>
    </div>
  );
};



const TechJobs = () => {
  const { permissions } = useTechPermissions();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [callMaskingEnabled, setCallMaskingEnabled] = useState(false);
  const [activeTab, setActiveTab] = useState("active");
  const [jobView, setJobView] = useState("list");
  const [selectedJob, setSelectedJob] = useState(null);
  const [callingCustomer, setCallingCustomer] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [customStatusOptions, setCustomStatusOptions] = useState([]);
  useEffect(() => {
    fetchJobStatuses("tech").then((list) => {
      setCustomStatusOptions((list || [])
        .filter((s) => !s.system && !s.archived && s.category === "active")
        .map((s) => ({ value: s.id, label: s.label, icon: Tag, color: s.color })));
    });
  }, []);
  const [respondingJob, setRespondingJob] = useState(null);
  const [rejectingJobId, setRejectingJobId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [completingJob, setCompletingJob] = useState(null); // job object for completion modal
  const [statusNoteJob, setStatusNoteJob] = useState(null); // {job, status} for status-change note modal
  const [submittingStatusNote, setSubmittingStatusNote] = useState(false);
  const [invoicingJob, setInvoicingJob] = useState(null); // job object for invoice modal
  const [docFormJob, setDocFormJob] = useState(null); // {job, type: "estimate"|"invoice"} for tech doc create modal
  const [linkedDocs, setLinkedDocs] = useState([]); // docs linked to currently selected job
  const [linkedDocsLoading, setLinkedDocsLoading] = useState(false);
  const [docStatusUpdating, setDocStatusUpdating] = useState(null); // invoice_id currently being updated
  const [payDoc, setPayDoc] = useState(null); // doc being marked paid via the picker
  const [collectDoc, setCollectDoc] = useState(null); // doc for Collect Payment (Stripe) modal
  const [submittingCompletion, setSubmittingCompletion] = useState(false);
  const [submittingInvoice, setSubmittingInvoice] = useState(false);
  const [submittingDoc, setSubmittingDoc] = useState(false);
  const [sigDoc, setSigDoc] = useState(null); // doc for Send-for-Signature modal
  const [ticketCopied, setTicketCopied] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);

  const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("tech_token")}` }
  });

  useEffect(() => { fetchJobs(); }, [activeTab]);

  const _getScheduledMs = (job) => {
    if (!job.scheduled_date) return null;
    const d = new Date(job.scheduled_date);
    if (isNaN(d.getTime())) return null;
    const st = (job.scheduled_time || "").trim();
    if (st) {
      const ampm = st.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
      if (ampm) {
        let h = parseInt(ampm[1], 10);
        const m = parseInt(ampm[2], 10);
        const p = ampm[3].toUpperCase();
        if (p === "PM" && h < 12) h += 12;
        if (p === "AM" && h === 12) h = 0;
        const base = new Date(d); base.setHours(h, m, 0, 0);
        return base.getTime();
      }
      const h24 = st.match(/^(\d{1,2}):(\d{2})$/);
      if (h24) {
        const base = new Date(d); base.setHours(parseInt(h24[1],10), parseInt(h24[2],10), 0, 0);
        return base.getTime();
      }
    }
    return d.getTime();
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      let status = null;
      if (activeTab === "active") status = "in_progress";
      else if (activeTab === "scheduled") status = "scheduled";
      else if (activeTab === "completed") status = "completed";

      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (activeTab === "pending") params.append("assignment_status", "pending");

      const res = await axios.get(`${API}/api/tech/jobs?${params.toString()}`, getAuthConfig());
      // Sort by full scheduled datetime, newest first
      const sorted = (res.data.jobs || []).sort((a, b) => {
        const aMs = _getScheduledMs(a);
        const bMs = _getScheduledMs(b);
        if (aMs === null && bMs === null) return 0;
        if (aMs === null) return 1;
        if (bMs === null) return -1;
        if (aMs !== bMs) return bMs - aMs;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      setJobs(sorted);
      setCallMaskingEnabled(res.data.call_masking_enabled);
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const callCustomer = async (jobId) => {
    setCallingCustomer(true);
    try {
      const res = await axios.post(`${API}/api/tech/jobs/${jobId}/call-customer`, {}, getAuthConfig());
      if (res.data.dial_number) {
        // Job-source direct line — dial number + extension, no masking
        toast.success(res.data.message || "Calling the job source line directly", { duration: 8000 });
        window.location.href = `tel:${res.data.dial_number.replace(/[^0-9+,#]/g, "")}`;
      } else if (res.data.masked_call_number_with_pin) {
        toast.success(res.data.message, { duration: 8000 });
        window.location.href = `tel:${res.data.masked_call_number_with_pin.replace(/[^0-9,#]/g, "")}`;
        fetchJobs();
      } else if (res.data.call_masking) toast.success(res.data.message);
      else toast.info(`Customer phone: ${res.data.customer_phone}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate call");
    } finally {
      setCallingCustomer(false);
    }
  };

  const updateJobStatus = async (jobId, newStatus) => {
    // For completion, open the modal instead of directly updating
    if (newStatus === "completed") {
      const job = jobs.find(j => j.job_id === jobId);
      setCompletingJob(job || { job_id: jobId });
      return;
    }
    // Estimate / Deposit Collected / Cancelled → ask for a note + price given
    if (["estimate", "deposit_collected", "cancelled"].includes(newStatus)) {
      const job = jobs.find(j => j.job_id === jobId);
      setStatusNoteJob({ job: job || { job_id: jobId }, status: newStatus });
      return;
    }
    setUpdatingStatus(jobId);
    try {
      await axios.patch(`${API}/api/tech/jobs/${jobId}/status`, { status: newStatus }, getAuthConfig());
      const label = newStatus === "in_progress" ? "started"
        : newStatus === "estimate" ? "marked as estimate"
        : newStatus === "cancelled" ? "cancelled"
        : newStatus.replace("_", " ");
      toast.success(`Job ${label}!`);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleStatusNoteSubmit = async (data) => {
    if (!statusNoteJob) return;
    const jobId = statusNoteJob.job.job_id;
    setSubmittingStatusNote(true);
    try {
      await axios.patch(`${API}/api/tech/jobs/${jobId}/status`, data, getAuthConfig());
      const label = data.status === "estimate" ? "marked as estimate"
        : data.status === "cancelled" ? "cancelled"
        : data.status.replace("_", " ");
      toast.success(`Job ${label}!`);
      setStatusNoteJob(null);
      if (selectedJob?.job_id === jobId) { setSelectedJob(null); setLinkedDocs([]); }
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setSubmittingStatusNote(false);
    }
  };

  const copyTicket = async (jobId) => {
    setTicketLoading(true);
    // Do NOT await before copyTextAsync — iOS Safari needs the clipboard call
    // to start inside the user gesture.
    const token = localStorage.getItem("tech_token");
    let maskingWarning = null;
    try {
      const ok = await copyTextAsync(async () => {
        const res = await axios.get(`${API}/api/tech/jobs/${jobId}/ticket-text`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        maskingWarning = res.data?.masking_warning || null;
        return res.data?.ticket_text || "";
      });
      if (!ok) throw new Error("clipboard unavailable");
      setTicketCopied(true);
      if (maskingWarning) toast.warning(maskingWarning, { duration: 9000 });
      toast.success("Ticket copied to clipboard");
      setTimeout(() => setTicketCopied(false), 2000);
    } catch {
      toast.error("Failed to copy ticket");
    } finally {
      setTicketLoading(false);
    }
  };

  const handleCompleteJob = async (data) => {
    if (!completingJob) return;
    setSubmittingCompletion(true);
    try {
      await axios.patch(`${API}/api/tech/jobs/${completingJob.job_id}/status`, data, getAuthConfig());
      toast.success("Job completed!");
      setCompletingJob(null);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete job");
    } finally {
      setSubmittingCompletion(false);
    }
  };

  const handleSendInvoice = async (data) => {
    setSubmittingInvoice(true);
    try {
      await axios.post(`${API}/api/tech/jobs/${data.job_id}/quick-invoice`, data, getAuthConfig());
      toast.success(data.customer_email ? "Invoice sent!" : "Invoice created!");
      setInvoicingJob(null);
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSubmittingInvoice(false);
    }
  };

  const fetchLinkedDocs = async (jobId) => {
    if (!jobId) return;
    setLinkedDocsLoading(true);
    try {
      const res = await axios.get(`${API}/api/tech/documents?job_id=${jobId}`, getAuthConfig());
      setLinkedDocs(res.data.documents || []);
    } catch {
      setLinkedDocs([]);
    } finally {
      setLinkedDocsLoading(false);
    }
  };

  // Live signature updates — silently refresh linked docs while a signature is pending
  useEffect(() => {
    if (!selectedJob) return undefined;
    const pending = linkedDocs.some((d) => ["sent", "viewed"].includes(d.signature_display_status));
    if (!pending) return undefined;
    const t = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/api/tech/documents?job_id=${selectedJob.job_id}`, getAuthConfig());
        setLinkedDocs(res.data.documents || []);
      } catch { /* ignore */ }
    }, 10000);
    return () => clearInterval(t);
  }, [selectedJob, linkedDocs]);

  // Update a tech-created document's status (Send / Mark Paid / Accept / Decline).
  // Backend: PATCH /api/tech/documents/{id}/status — accepts {status, payment_method?}
  const updateDocStatus = async (doc, status, extra = {}) => {
    setDocStatusUpdating(doc.invoice_id);
    try {
      await axios.patch(
        `${API}/api/tech/documents/${doc.invoice_id}/status`,
        { status, ...extra },
        getAuthConfig(),
      );
      const noun = (doc.document_type || "invoice") === "invoice" ? "Invoice" : "Estimate";
      const verb = status === "sent" ? "sent"
                 : status === "paid" ? "marked as paid"
                 : status === "accepted" ? "accepted"
                 : status === "declined" ? "declined"
                 : `set to ${status}`;
      toast.success(`${noun} ${verb}`);
      await fetchLinkedDocs(doc.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    } finally {
      setDocStatusUpdating(null);
    }
  };

  const handleCreateDoc = async (data) => {
    setSubmittingDoc(true);
    try {
      const res = await axios.post(`${API}/api/tech/documents`, data, getAuthConfig());
      const isEstimate = data.document_type === "estimate";
      const num = res.data?.document?.invoice_number || "";
      toast.success(`${isEstimate ? "Estimate" : "Invoice"} ${num} created!`);
      const closingJobId = docFormJob?.job?.job_id;
      setDocFormJob(null);
      // Refresh linked docs if the detail modal is still open for this job
      if (closingJobId && selectedJob?.job_id === closingJobId) {
        fetchLinkedDocs(closingJobId);
      }
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create document");
    } finally {
      setSubmittingDoc(false);
    }
  };

  const respondToJob = async (jobId, action, reason = null) => {
    setRespondingJob(jobId);
    try {
      const payload = { action };
      if (reason) payload.rejection_reason = reason;
      const res = await axios.post(`${API}/api/tech/jobs/${jobId}/respond`, payload, getAuthConfig());
      toast.success(res.data.message);
      setRejectingJobId(null);
      setRejectionReason("");
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} job`);
    } finally {
      setRespondingJob(null);
    }
  };

  const canSetStatus = enabled(permissions, "set_job_status");

  const setJobStatusDirect = (jobId, status) => {
    updateJobStatus(jobId, status);
    return true;
  };

  const getSwipeAction = (job) => {
    if (job.assignment_status === "pending") return null;
    if (job.status === "scheduled") return {
      label: "Start Job",
      icon: <Play className="w-5 h-5" />,
      color: "#3B82F6",
      newStatus: "in_progress",
      testId: "start",
    };
    if (job.status === "in_progress") return {
      label: "Complete",
      icon: <CheckCircle className="w-5 h-5" />,
      color: "#22C55E",
      newStatus: "completed",
      testId: "complete",
    };
    return null;
  };

  const getStatusColor = (status) => {
    const dyn = getDynamicStatuses()[status];
    if (dyn?.color) return dyn.color;
    const map = { pending: "#F59E0B", estimate: "#F97316", scheduled: "#3B82F6", in_progress: "#A855F7", deposit_collected: "#14B8A6", completed: "#22C55E", completed_pending_payment: "#F59E0B", cancelled: "#EF4444" };
    return map[status] || "#6B7280";
  };

  const viewJobDetails = async (jobId) => {
    try {
      const res = await axios.get(`${API}/api/tech/jobs/${jobId}`, getAuthConfig());
      setSelectedJob(res.data);
      fetchLinkedDocs(jobId);
    } catch (error) {
      if (error.response?.status === 403) toast.error("You must accept this job before viewing details");
      else toast.error("Failed to load job details");
    }
  };

  const tabs = [
    { key: "pending", label: "Pending", color: "#F59E0B" },
    { key: "scheduled", label: "Scheduled", color: "#3B82F6" },
    { key: "active", label: "In Progress", color: "#A855F7" },
    { key: "completed", label: "Completed", color: "#22C55E" },
  ];

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="tech-jobs">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-foreground font-['Manrope']">My Jobs</h1>
        <p className="text-muted-foreground/60 text-sm mt-0.5">View and manage your assigned jobs</p>
      </div>

      {/* View toggle + Tabs */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-xl border border-border/60 overflow-hidden" data-testid="job-view-toggle">
          <button
            onClick={() => setJobView("list")}
            className={`px-3 py-2 text-xs sm:text-sm font-medium flex items-center gap-1.5 ${jobView === "list" ? "bg-primary/15 text-primary" : "bg-muted/40 text-muted-foreground"}`}
            data-testid="job-view-list-btn"
          >
            <ListIcon className="w-3.5 h-3.5" /> List
          </button>
          <button
            onClick={() => setJobView("map")}
            className={`px-3 py-2 text-xs sm:text-sm font-medium flex items-center gap-1.5 ${jobView === "map" ? "bg-primary/15 text-primary" : "bg-muted/40 text-muted-foreground"}`}
            data-testid="job-view-map-btn"
          >
            <MapIcon className="w-3.5 h-3.5" /> Map
          </button>
        </div>
        {jobView === "list" && (
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none" data-testid="job-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`relative px-4 py-2 rounded-xl text-xs sm:text-sm font-medium whitespace-nowrap transition-all duration-200 border ${
                activeTab === tab.key
                  ? ""
                  : "bg-muted/50 text-muted-foreground border-border/50 hover:bg-muted"
              }`}
              style={activeTab === tab.key ? {background: `${tab.color}12`, color: tab.color, borderColor: `${tab.color}25`} : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>
        )}
      </div>

      {/* Map view */}
      {jobView === "map" && (
        <TechJobMap onOpenJob={(jobId) => viewJobDetails(jobId)} />
      )}

      {/* Jobs List */}
      {jobView === "list" && (loading ? (
        <div className="flex items-center justify-center h-48">
          <Loader2 className="w-6 h-6 text-primary animate-spin" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-muted/30 border border-border/50">
          <Briefcase className="w-10 h-10 mx-auto mb-3 text-muted-foreground/20" />
          <p className="text-muted-foreground text-sm">No {activeTab} jobs</p>
        </div>
      ) : (
        <div className="space-y-3">
          {jobs.map((job) => {
            const action = getSwipeAction(job);
            return (
            <SwipeableJobCard
              key={job.job_id}
              action={action}
              onAction={() => action && updateJobStatus(job.job_id, action.newStatus)}
              disabled={updatingStatus === job.job_id}
            >
            <div className="group relative rounded-2xl p-4 sm:p-5 transition-all duration-200 hover:-translate-y-0.5 bg-card border border-border/60 shadow-sm" data-testid={`job-card-${job.job_id}`}>
              {updatingStatus === job.job_id && (
                <div className="absolute inset-0 bg-black/20 dark:bg-black/40 rounded-2xl flex items-center justify-center z-20">
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                </div>
              )}
              <div className="absolute left-0 top-4 bottom-4 w-0.5 rounded-full" style={{background: getStatusColor(job.status)}} />
              <div className="pl-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start sm:items-center gap-2 mb-2 flex-wrap">
                      <h3 className="text-[15px] sm:text-base font-semibold text-foreground">{job.title}</h3>
                      {job.job_display_id && (
                        <span className="text-[10px] font-mono text-muted-foreground/50 bg-muted px-1.5 py-0.5 rounded">{job.job_display_id}</span>
                      )}
                      <div className="flex gap-1.5 flex-wrap">
                        <Badge className="text-[10px] px-2 py-0.5 border" style={{background: `${getStatusColor(job.status)}10`, color: getStatusColor(job.status), borderColor: `${getStatusColor(job.status)}20`}}>
                          {getStatusConfig(job.status).label}
                        </Badge>
                        {job.assignment_status === "pending" && !isFinishedStatus(job.status) && (
                          <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 text-[10px] px-2 py-0.5 border">Awaiting Approval</Badge>
                        )}
                        {job.attachment_count > 0 && (
                          <Badge
                            className="bg-sky-500/10 text-sky-500 border-sky-500/20 text-[10px] px-2 py-0.5 border flex items-center gap-1"
                            data-testid={`job-attachments-badge-${job.job_id}`}
                            title={`${job.attachment_count} attachment${job.attachment_count > 1 ? "s" : ""} on this job`}
                          >
                            {job.photo_count > 0 ? <Camera className="w-3 h-3" /> : <Paperclip className="w-3 h-3" />}
                            {job.photo_count > 0 && job.photo_count !== job.attachment_count
                              ? `${job.photo_count} photo${job.photo_count > 1 ? "s" : ""} +${job.attachment_count - job.photo_count}`
                              : job.attachment_count}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <User className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{job.customer_name}</span>
                      </div>
                      {job.customer_address && job.assignment_status !== "pending" && (
                        <div className="flex items-start gap-2 text-muted-foreground/60">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-1">{job.customer_address}</span>
                        </div>
                      )}
                      {job.scheduled_date && (
                        <div className="flex items-center gap-2 text-muted-foreground/60 flex-wrap">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDateWithDay(job.scheduled_date)}</span>
                          {(job.scheduled_time || job.scheduled_date) && (formatJobScheduleDisplay(job)) && (
                            <><Clock className="w-3.5 h-3.5 ml-1" /><span>{formatJobScheduleDisplay(job)}</span></>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-row sm:flex-col gap-2 mt-1 sm:mt-0">
                    {job.assignment_status !== "pending" && (
                      <>
                        {action && (
                          <Button
                            size="sm"
                            onClick={() => updateJobStatus(job.job_id, action.newStatus)}
                            disabled={updatingStatus === job.job_id}
                            className="text-xs h-8 rounded-lg border"
                            style={{
                              background: `${action.color}12`,
                              color: action.color,
                              borderColor: `${action.color}25`,
                            }}
                            data-testid={`quick-${action.testId}-${job.job_id}`}
                          >
                            {updatingStatus === job.job_id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : action.icon && React.cloneElement(action.icon, { className: "w-3.5 h-3.5 mr-1" })
                            }
                            <span className="hidden sm:inline">{action.label}</span>
                          </Button>
                        )}
                        {getStatusCategory(job.status) === "completed" && (
                          <Button
                            size="sm"
                            onClick={() => setInvoicingJob(job)}
                            className="text-xs h-8 rounded-lg border bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 border-blue-500/20"
                            data-testid={`invoice-btn-${job.job_id}`}
                          >
                            <FileText className="w-3.5 h-3.5 mr-1" />
                            <span className="hidden sm:inline">Invoice</span>
                          </Button>
                        )}
                        {canSetStatus && !isFinishedStatus(job.status) && (
                          <JobStatusMenu job={job} updating={updatingStatus === job.job_id} onSet={setJobStatusDirect} extraOptions={customStatusOptions} />
                        )}
                        {job.direct_dial ? (
                          <>
                            <a href={`tel:${job.direct_dial.replace(/[^0-9+,#]/g, '')}`}>
                              <Button size="sm" className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 text-xs h-8 rounded-lg" data-testid={`call-btn-${job.job_id}`} title="Job source line — dials the extension automatically">
                                <PhoneCall className="w-3.5 h-3.5 mr-1" />
                                <span className="hidden sm:inline text-[10px]">{job.customer_phone} ext. {job.customer_phone_ext}</span>
                                <span className="sm:hidden">Call</span>
                              </Button>
                            </a>
                            {job.direct_dial2 ? (
                              <a href={`tel:${job.direct_dial2.replace(/[^0-9+,#]/g, '')}`}>
                                <Button size="sm" className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-500 border border-sky-500/20 text-xs h-8 rounded-lg" data-testid={`call2-btn-${job.job_id}`}>
                                  <PhoneCall className="w-3.5 h-3.5 mr-1" />
                                  <span className="hidden sm:inline text-[10px]">2nd: {job.customer_phone2} ext. {job.customer_phone2_ext}</span>
                                  <span className="sm:hidden">Call 2</span>
                                </Button>
                              </a>
                            ) : job.masked_call_number2_with_pin && (
                              <a href={`tel:${job.masked_call_number2_with_pin.replace(/[^0-9,#]/g, '')}`}>
                                <Button size="sm" className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-500 border border-sky-500/20 text-xs h-8 rounded-lg" data-testid={`call2-btn-${job.job_id}`}>
                                  <PhoneCall className="w-3.5 h-3.5 mr-1" />
                                  <span className="hidden sm:inline text-[10px]">2nd: {job.masked_call_number2_with_pin}</span>
                                  <span className="sm:hidden">Call 2</span>
                                </Button>
                              </a>
                            )}
                          </>
                        ) : callMaskingEnabled && job.masked_call_number_with_pin ? (
                          <>
                            <a href={`tel:${job.masked_call_number_with_pin.replace(/[^0-9,#]/g, '')}`}>
                              <Button size="sm" className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 text-xs h-8 rounded-lg" data-testid={`call-btn-${job.job_id}`}>
                                <PhoneCall className="w-3.5 h-3.5 mr-1" />
                                <span className="hidden sm:inline text-[10px]">{job.masked_call_number_with_pin}</span>
                                <span className="sm:hidden">Call</span>
                              </Button>
                            </a>
                            {job.masked_call_number2_with_pin && (
                              <a href={`tel:${job.masked_call_number2_with_pin.replace(/[^0-9,#]/g, '')}`}>
                                <Button size="sm" className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-500 border border-sky-500/20 text-xs h-8 rounded-lg" data-testid={`call2-btn-${job.job_id}`}>
                                  <PhoneCall className="w-3.5 h-3.5 mr-1" />
                                  <span className="hidden sm:inline text-[10px]">2nd: {job.masked_call_number2_with_pin}</span>
                                  <span className="sm:hidden">Call 2</span>
                                </Button>
                              </a>
                            )}
                          </>
                        ) : callMaskingEnabled ? (
                          <Button size="sm" onClick={() => callCustomer(job.job_id)} disabled={callingCustomer} className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 text-xs h-8 rounded-lg" data-testid={`call-btn-${job.job_id}`}>
                            <PhoneCall className="w-3.5 h-3.5 mr-1" />
                            <span className="hidden sm:inline">Call</span>
                          </Button>
                        ) : job.customer_phone ? (
                          <a href={`tel:${job.customer_phone}${job.customer_phone_ext ? `,,${job.customer_phone_ext}` : ""}`}>
                            <Button size="sm" className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 text-xs h-8 w-full rounded-lg">
                              <Phone className="w-3.5 h-3.5 mr-1" />
                              <span className="hidden sm:inline">Call</span>
                            </Button>
                          </a>
                        ) : job.phone_hidden ? (
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground/50 px-2 h-8" data-testid={`phone-hidden-${job.job_id}`}>
                            <ShieldOff className="w-3 h-3" /> Contact protected
                          </span>
                        ) : null}
                        <Button size="sm" variant="ghost" onClick={() => viewJobDetails(job.job_id)} className="text-muted-foreground hover:text-foreground text-xs h-8 rounded-lg" data-testid={`details-btn-${job.job_id}`}>
                          Details <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                        </Button>
                      </>
                    )}
                    {job.assignment_status === "pending" && !isFinishedStatus(job.status) && (
                      <div className="flex flex-col gap-2 w-full sm:w-auto">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => respondToJob(job.job_id, "accept")}
                            disabled={respondingJob === job.job_id}
                            className="text-xs h-8 rounded-lg bg-emerald-500/12 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/25 flex-1 sm:flex-none"
                            data-testid={`accept-btn-${job.job_id}`}
                          >
                            {respondingJob === job.job_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5 mr-1" />}
                            Accept
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setRejectingJobId(rejectingJobId === job.job_id ? null : job.job_id)}
                            disabled={respondingJob === job.job_id}
                            className="text-xs h-8 rounded-lg bg-red-500/12 hover:bg-red-500/20 text-red-500 border border-red-500/25 flex-1 sm:flex-none"
                            data-testid={`reject-btn-${job.job_id}`}
                          >
                            <ThumbsDown className="w-3.5 h-3.5 mr-1" />
                            Reject
                          </Button>
                        </div>
                        {rejectingJobId === job.job_id && (
                          <div className="flex gap-2 items-center" data-testid={`reject-form-${job.job_id}`}>
                            <input
                              type="text"
                              value={rejectionReason}
                              onChange={(e) => setRejectionReason(e.target.value)}
                              placeholder="Reason (optional)"
                              className="flex-1 h-8 px-3 text-xs rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-red-500/30"
                              data-testid={`reject-reason-${job.job_id}`}
                            />
                            <Button
                              size="sm"
                              onClick={() => respondToJob(job.job_id, "reject", rejectionReason || null)}
                              disabled={respondingJob === job.job_id}
                              className="text-xs h-8 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-500 border border-red-500/30"
                              data-testid={`confirm-reject-${job.job_id}`}
                            >
                              {respondingJob === job.job_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Confirm"}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {action && (
                  <div className="flex items-center gap-1.5 mt-2.5 sm:hidden" data-testid={`swipe-hint-${job.job_id}`}>
                    <ArrowRight className="w-3 h-3 text-muted-foreground/20 animate-pulse" />
                    <span className="text-[10px] text-muted-foreground/30">Swipe right to {action.label.toLowerCase()}</span>
                  </div>
                )}
              </div>
            </div>
            </SwipeableJobCard>
            );
          })}
        </div>
      ))}

      {/* Job Detail Modal */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/30 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4" onClick={() => { setSelectedJob(null); setLinkedDocs([]); }} data-testid="job-detail-modal">
          <div className="relative rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-auto bg-background border border-border shadow-2xl" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="sticky top-0 z-10 p-4 sm:p-5 flex items-start justify-between gap-3 bg-background border-b border-border">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-foreground font-['Manrope']">{selectedJob.title}</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  {selectedJob.job_display_id && <span className="text-[11px] font-mono text-muted-foreground/50">{selectedJob.job_display_id}</span>}
                  {selectedJob.service_name && <span className="text-muted-foreground text-sm">{selectedJob.service_name}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canSetStatus && !["completed", "completed_pending_payment", "cancelled"].includes(selectedJob.status) && (
                  <JobStatusMenu
                    job={selectedJob}
                    updating={updatingStatus === selectedJob.job_id}
                    onSet={setJobStatusDirect}
                    extraOptions={customStatusOptions}
                  />
                )}
                <Badge className="text-xs px-2.5 py-1 border" style={{background: `${getStatusColor(selectedJob.status)}10`, color: getStatusColor(selectedJob.status), borderColor: `${getStatusColor(selectedJob.status)}20`}}>
                  {getStatusConfig(selectedJob.status).label}
                </Badge>
                <button onClick={() => { setSelectedJob(null); setLinkedDocs([]); }} className="text-muted-foreground hover:text-foreground transition-colors p-1" data-testid="close-detail-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-5 space-y-4">
              {/* Customer Info */}
              <div className="rounded-xl p-4 bg-card border border-border/50">
                <h4 className="text-muted-foreground/70 text-xs font-medium uppercase tracking-wider mb-3 flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-primary" /> Customer
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="text-foreground font-medium">{selectedJob.customer_name}</p>
                  {getDisplayAddress(selectedJob) && (
                    <div className="space-y-1.5">
                      <div className="flex items-start gap-2 text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                        <span data-testid="tech-job-full-address">{getDisplayAddress(selectedJob)}</span>
                      </div>
                      <div className="flex items-center gap-2 pl-5.5" style={{ paddingLeft: "1.375rem" }}>
                        <a
                          href={getGoogleMapsUrl(selectedJob) || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(getDisplayAddress(selectedJob))}`}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors"
                          data-testid="tech-google-maps-btn"
                        >
                          <Navigation className="w-3 h-3" /> Google Maps <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                        <a
                          href={`https://maps.apple.com/?q=${encodeURIComponent(getDisplayAddress(selectedJob))}`}
                          target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium bg-gray-500/10 text-gray-600 dark:text-gray-400 hover:bg-gray-500/20 transition-colors"
                          data-testid="tech-apple-maps-btn"
                        >
                          <Navigation className="w-3 h-3" /> Apple Maps <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      </div>
                    </div>
                  )}
                  <CustomerEmailEditor
                    key={selectedJob.job_id}
                    job={selectedJob}
                    canAdd={enabled(permissions, "add_customer_email")}
                    canEdit={enabled(permissions, "edit_customer_email")}
                    onSaved={(email) => setSelectedJob((j) => ({ ...j, customer_email: email }))}
                  />
                  <div className="pt-2">
                    {selectedJob.customer_phone_ext && (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-xs px-2 py-1 mb-2 rounded-md bg-amber-500/10 text-amber-500 border border-amber-500/20 hover:bg-amber-500/20"
                        title="Copy extension for manual dialing"
                        onClick={() => { navigator.clipboard.writeText(selectedJob.customer_phone_ext); toast.success("Extension copied"); }}
                        data-testid="modal-phone-ext"
                      >
                        Ext. {selectedJob.customer_phone_ext}
                        <Copy className="w-3 h-3" />
                      </button>
                    )}
                    {selectedJob.direct_dial ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <a href={`tel:${selectedJob.direct_dial.replace(/[^0-9+,#]/g, '')}`} className="block sm:inline-block">
                          <Button size="sm" className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 rounded-lg w-full sm:w-auto" data-testid="modal-call-btn" title="Job source line — dials the extension automatically">
                            <PhoneCall className="w-4 h-4 mr-2" />{selectedJob.customer_phone} ext. {selectedJob.customer_phone_ext}
                          </Button>
                        </a>
                        {selectedJob.direct_dial2 ? (
                          <a href={`tel:${selectedJob.direct_dial2.replace(/[^0-9+,#]/g, '')}`} className="block sm:inline-block">
                            <Button size="sm" className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-500 border border-sky-500/20 rounded-lg w-full sm:w-auto" data-testid="modal-call2-btn">
                              <PhoneCall className="w-4 h-4 mr-2" />{selectedJob.customer_phone2} ext. {selectedJob.customer_phone2_ext}
                              <span className="ml-1.5 text-[10px] opacity-70">2nd number</span>
                            </Button>
                          </a>
                        ) : selectedJob.masked_call_number2_with_pin && (
                          <a href={`tel:${selectedJob.masked_call_number2_with_pin.replace(/[^0-9,#]/g, '')}`} className="block sm:inline-block">
                            <Button size="sm" className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-500 border border-sky-500/20 rounded-lg w-full sm:w-auto" data-testid="modal-call2-btn">
                              <PhoneCall className="w-4 h-4 mr-2" />{selectedJob.masked_call_number2_with_pin}
                              <span className="ml-1.5 text-[10px] opacity-70">2nd number</span>
                            </Button>
                          </a>
                        )}
                      </div>
                    ) : selectedJob.call_masking_enabled && selectedJob.masked_call_number_with_pin ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <a href={`tel:${selectedJob.masked_call_number_with_pin.replace(/[^0-9,#]/g, '')}`} className="block sm:inline-block">
                          <Button size="sm" className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 rounded-lg w-full sm:w-auto" data-testid="modal-call-btn">
                            <PhoneCall className="w-4 h-4 mr-2" />{selectedJob.masked_call_number_with_pin}
                          </Button>
                        </a>
                        {selectedJob.masked_call_number2_with_pin && (
                          <a href={`tel:${selectedJob.masked_call_number2_with_pin.replace(/[^0-9,#]/g, '')}`} className="block sm:inline-block">
                            <Button size="sm" className="bg-sky-500/10 hover:bg-sky-500/20 text-sky-500 border border-sky-500/20 rounded-lg w-full sm:w-auto" data-testid="modal-call2-btn">
                              <PhoneCall className="w-4 h-4 mr-2" />{selectedJob.masked_call_number2_with_pin}
                              <span className="ml-1.5 text-[10px] opacity-70">2nd number</span>
                            </Button>
                          </a>
                        )}
                      </div>
                    ) : selectedJob.call_masking_enabled ? (
                      <Button size="sm" onClick={() => callCustomer(selectedJob.job_id)} disabled={callingCustomer} className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 rounded-lg w-full sm:w-auto" data-testid="modal-call-btn">
                        {callingCustomer ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PhoneCall className="w-4 h-4 mr-2" />}
                        Call Customer (Masked)
                      </Button>
                    ) : selectedJob.customer_phone ? (
                      <a href={`tel:${selectedJob.customer_phone}${selectedJob.customer_phone_ext ? `,,${selectedJob.customer_phone_ext}` : ""}`} className="block sm:inline-block">
                        <Button size="sm" className="bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 rounded-lg w-full sm:w-auto">
                          <Phone className="w-4 h-4 mr-2" />{selectedJob.customer_phone}{selectedJob.customer_phone_ext ? ` ext. ${selectedJob.customer_phone_ext}` : ""}
                        </Button>
                      </a>
                    ) : selectedJob.phone_hidden ? (
                      <div className="flex items-center gap-2 text-muted-foreground/60 text-xs bg-muted/40 border border-border/50 rounded-lg p-2.5" data-testid="modal-phone-hidden">
                        <ShieldOff className="w-3.5 h-3.5 shrink-0" />
                        <span>Customer contact info is protected by your administrator. Direct calling is unavailable.</span>
                      </div>
                    ) : <p className="text-muted-foreground/40 text-xs">No phone number available</p>}
                  </div>
                </div>
              </div>

              {/* Schedule */}
              <div className="rounded-xl p-4 bg-card border border-border/50">
                <h4 className="text-muted-foreground/70 text-xs font-medium uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5 text-blue-500" /> Schedule
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedJob.scheduled_date && (
                    <div><p className="text-muted-foreground/60 text-xs mb-0.5">Date</p><p className="text-foreground font-medium">{formatDateWithDay(selectedJob.scheduled_date)}</p></div>
                  )}
                  {(selectedJob.scheduled_time || selectedJob.scheduled_date) && (formatJobScheduleDisplay(selectedJob)) && (
                    <div><p className="text-muted-foreground/60 text-xs mb-0.5">Time</p><p className="text-foreground font-medium">{formatJobScheduleDisplay(selectedJob)}</p></div>
                  )}
                </div>
              </div>

              {/* Vehicle */}
              {(selectedJob.vehicle_make || selectedJob.vehicle_model || selectedJob.vehicle_year) && (
                <div className="rounded-xl p-4 bg-orange-50/50 dark:bg-orange-500/[0.04] border border-orange-200/50 dark:border-orange-500/12">
                  <h4 className="text-muted-foreground/70 text-xs font-medium uppercase tracking-wider mb-2 flex items-center gap-2">
                    <Wrench className="w-3.5 h-3.5 text-orange-500" /> Vehicle
                  </h4>
                  <p className="text-foreground font-medium text-sm">
                    {[selectedJob.vehicle_year, selectedJob.vehicle_make, selectedJob.vehicle_model].filter(Boolean).join(" ")}
                  </p>
                </div>
              )}

              {/* Notes */}
              {selectedJob.notes && (
                <div className="rounded-xl p-4 bg-card border border-border/50">
                  <h4 className="text-muted-foreground/70 text-xs font-medium uppercase tracking-wider mb-2">Notes</h4>
                  <p className="text-muted-foreground text-sm whitespace-pre-wrap">{selectedJob.notes}</p>
                </div>
              )}

              {/* Attachments — technicians can upload job photos/PDFs */}
              <JobAttachments jobId={selectedJob.job_id} tokenKey="tech_token" />

              {/* Parts */}
              {selectedJob.parts?.length > 0 && (
                <div className="rounded-xl p-4 bg-card border border-border/50">
                  <h4 className="text-muted-foreground/70 text-xs font-medium uppercase tracking-wider mb-3">Parts Used</h4>
                  <div className="space-y-2">
                    {selectedJob.parts.map((part, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm p-2.5 rounded-lg bg-muted/50">
                        <div>
                          <span className="text-foreground">{part.name}</span>
                          {part.quantity > 1 && <span className="text-muted-foreground/50 ml-1.5">x{part.quantity}</span>}
                        </div>
                        <div className="text-right">
                          <span className="text-foreground font-medium">${(part.cost * (part.quantity || 1)).toFixed(2)}</span>
                          <span className="text-muted-foreground/40 text-xs ml-2">({part.provider})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing */}
              {(selectedJob.status === "completed" || selectedJob.status === "completed_pending_payment") && selectedJob.total_price && (
                <div className="rounded-xl p-4 bg-emerald-50/50 dark:bg-emerald-500/[0.04] border border-emerald-200/50 dark:border-emerald-500/12">
                  <h4 className="text-muted-foreground/70 text-xs font-medium uppercase tracking-wider mb-3">Job Summary</h4>
                  <div className="space-y-2 text-sm">
                    {selectedJob.labor_cost && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Labor</span><span className="text-foreground">${selectedJob.labor_cost.toFixed(2)}</span></div>
                    )}
                    {selectedJob.parts_cost && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Parts</span><span className="text-foreground">${selectedJob.parts_cost.toFixed(2)}</span></div>
                    )}
                    <div className="flex justify-between pt-2 font-medium border-t border-border/50">
                      <span className="text-foreground">Total</span>
                      <span className="text-emerald-500">${selectedJob.total_price.toFixed(2)}</span>
                    </div>
                    {selectedJob.commission_earned && (
                      <div className="flex justify-between text-xs pt-1">
                        <span className="text-muted-foreground/60">Your Commission</span>
                        <span className="text-purple-500 font-semibold">${selectedJob.commission_earned.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Linked Documents (estimates + invoices linked to this job) */}
              {(linkedDocsLoading || linkedDocs.length > 0) && (
                <div className="rounded-xl p-4 bg-card border border-border/50" data-testid="tech-linked-documents">
                  <h4 className="text-muted-foreground/70 text-xs font-medium uppercase tracking-wider mb-3 flex items-center gap-2">
                    <FileCheck className="w-3.5 h-3.5 text-blue-500" /> Linked Documents
                    {!linkedDocsLoading && <span className="text-foreground/80">· {linkedDocs.length}</span>}
                  </h4>
                  {linkedDocsLoading ? (
                    <p className="text-xs text-muted-foreground">Loading…</p>
                  ) : (
                    <div className="space-y-1.5">
                      {linkedDocs.map((d) => {
                        const isInvoice = (d.document_type || "invoice") === "invoice";
                        const status = d.status || "draft";
                        const busy = docStatusUpdating === d.invoice_id;
                        // Status badge config
                        const statusColors = {
                          draft: "bg-muted text-muted-foreground",
                          sent: "bg-blue-500/15 text-blue-500",
                          paid: "bg-emerald-500/15 text-emerald-500",
                          partially_paid: "bg-amber-500/15 text-amber-500",
                          overdue: "bg-red-500/15 text-red-500",
                          cancelled: "bg-muted text-muted-foreground",
                          accepted: "bg-emerald-500/15 text-emerald-500",
                          declined: "bg-red-500/15 text-red-500",
                          converted: "bg-purple-500/15 text-purple-500",
                        };
                        const showSend = ["draft"].includes(status) && allowed(permissions, isInvoice ? "send_invoices" : "send_estimates");
                        const showMarkPaid = isInvoice && ["draft", "sent", "overdue"].includes(status) && allowed(permissions, "collect_payments");
                        const showCollect = isInvoice && !["paid", "cancelled", "converted"].includes(status)
                          && ((d.total || 0) - (d.amount_paid || 0)) > 0.005
                          && (allowed(permissions, "collect_payments") || allowed(permissions, "collect_deposits"));
                        const showAccept = !isInvoice && ["draft", "sent"].includes(status);
                        const showDecline = !isInvoice && ["draft", "sent"].includes(status);
                        const showSignature = enabled(permissions, isInvoice ? "send_invoices_for_signature" : "send_estimates_for_signature")
                          && d.signature_display_status !== "signed"
                          && !["converted", "declined", "cancelled", "paid"].includes(status);
                        return (
                          <div
                            key={d.invoice_id}
                            className="w-full px-2.5 py-2 rounded-md bg-muted/40 space-y-2"
                            data-testid={`tech-linked-doc-${d.invoice_id}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0">
                                {isInvoice
                                  ? <Receipt className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                                  : <FileCheck className="w-3.5 h-3.5 text-orange-500 shrink-0" />}
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">
                                    {isInvoice ? "Invoice" : "Estimate"}{" "}
                                    <span className="text-muted-foreground">#{d.invoice_number || d.invoice_id?.slice(0, 8)}</span>
                                  </p>
                                  <p className="text-[10px] text-muted-foreground">
                                    {d.created_at ? new Date(d.created_at).toLocaleDateString() : "—"}
                                    {d.payment_method && status === "paid" && ` · paid via ${d.payment_method}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <SignatureStatusBadge status={d.signature_display_status} />
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wider ${statusColors[status] || "bg-muted text-muted-foreground"}`}>
                                  {status}
                                </span>
                                <span className="text-sm font-semibold text-foreground tabular-nums">
                                  ${(d.total || 0).toFixed(2)}
                                </span>
                              </div>
                            </div>
                            {(d.amount_paid || 0) > 0 && status !== "paid" && (
                              <p className="pl-5 text-[10px] text-amber-500 font-medium" data-testid={`tech-job-doc-balance-${d.invoice_id}`}>
                                Paid ${(d.amount_paid || 0).toFixed(2)} · Balance ${Math.max((d.total || 0) - (d.amount_paid || 0), 0).toFixed(2)}
                              </p>
                            )}
                            {(showSend || showMarkPaid || showCollect || showAccept || showDecline || showSignature) && (
                              <div className="flex flex-wrap gap-1.5 pl-5">
                                {showSignature && (
                                  <button
                                    onClick={() => setSigDoc(d)}
                                    disabled={busy}
                                    className="text-[11px] px-2 py-1 rounded-md bg-[#FFCC00]/10 hover:bg-[#FFCC00]/20 text-[#FFCC00] border border-[#FFCC00]/20 disabled:opacity-50"
                                    data-testid={`doc-action-signature-${d.invoice_id}`}
                                  >
                                    <PenLine className="w-3 h-3 inline mr-1" />
                                    {["sent", "viewed", "expired"].includes(d.signature_display_status) ? "Resend Signature" : "Request Signature"}
                                  </button>
                                )}
                                {showSend && (
                                  <button
                                    onClick={() => updateDocStatus(d, "sent")}
                                    disabled={busy}
                                    className="text-[11px] px-2 py-1 rounded-md bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 border border-blue-500/20 disabled:opacity-50"
                                    data-testid={`doc-action-send-${d.invoice_id}`}
                                  >
                                    <Send className="w-3 h-3 inline mr-1" /> Send
                                  </button>
                                )}
                                {showCollect && (
                                  <button
                                    onClick={() => setCollectDoc(d)}
                                    disabled={busy}
                                    className="text-[11px] px-2 py-1 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 disabled:opacity-50"
                                    data-testid={`doc-action-collect-${d.invoice_id}`}
                                  >
                                    <CreditCard className="w-3 h-3 inline mr-1" /> Collect Payment
                                  </button>
                                )}
                                {showMarkPaid && (
                                  <button
                                    onClick={() => setPayDoc(d)}
                                    disabled={busy}
                                    className="text-[11px] px-2 py-1 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 disabled:opacity-50"
                                    data-testid={`doc-action-paid-${d.invoice_id}`}
                                  >
                                    <CheckCircle2 className="w-3 h-3 inline mr-1" /> Mark Paid
                                  </button>
                                )}
                                {showAccept && (
                                  <button
                                    onClick={() => updateDocStatus(d, "accepted")}
                                    disabled={busy}
                                    className="text-[11px] px-2 py-1 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 border border-emerald-500/20 disabled:opacity-50"
                                    data-testid={`doc-action-accept-${d.invoice_id}`}
                                  >
                                    <CheckCircle2 className="w-3 h-3 inline mr-1" /> Accept
                                  </button>
                                )}
                                {showDecline && (
                                  <button
                                    onClick={() => {
                                      const reason = window.prompt("Decline reason (optional):");
                                      if (reason === null) return; // user cancelled
                                      updateDocStatus(d, "declined", reason ? { decline_reason: reason } : {});
                                    }}
                                    disabled={busy}
                                    className="text-[11px] px-2 py-1 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 disabled:opacity-50"
                                    data-testid={`doc-action-decline-${d.invoice_id}`}
                                  >
                                    Decline
                                  </button>
                                )}
                                {busy && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
                              </div>
                            )}
                            {allowed(permissions, "view_payment_history") && (d.payments || []).length > 0 && (
                              <div className="pl-5 pt-1.5 space-y-1 border-t border-border/40" data-testid={`tech-doc-payments-${d.invoice_id}`}>
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Payment History</p>
                                {d.payments.map((p) => (
                                  <div key={p.payment_id} className="flex items-center justify-between text-[11px] gap-2">
                                    <span className="text-muted-foreground truncate">
                                      {[
                                        p.payment_date || (p.created_at || "").slice(0, 10),
                                        p.method,
                                        p.collected_by_name,
                                      ].filter(Boolean).join(" · ") || "Payment"}
                                    </span>
                                    <span className="font-semibold text-emerald-500 tabular-nums shrink-0">${(p.amount || 0).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 p-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 bg-background border-t border-border">
              {selectedJob.assignment_status !== "pending" && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() => copyTicket(selectedJob.job_id)}
                    disabled={ticketLoading}
                    className="text-xs h-9 rounded-lg border bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 border-amber-500/20"
                    data-testid="tech-copy-ticket-btn"
                  >
                    {ticketCopied ? <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                    {ticketCopied ? "Copied!" : "Copy Ticket"}
                  </Button>
                  {allowed(permissions, "create_estimates") && (
                  <Button
                    size="sm"
                    onClick={() => setDocFormJob({ job: selectedJob, type: "estimate" })}
                    className="text-xs h-9 rounded-lg border bg-orange-500/10 hover:bg-orange-500/20 text-orange-500 border-orange-500/20"
                    data-testid="create-estimate-btn"
                  >
                    <FileText className="w-3.5 h-3.5 mr-1.5" /> Create Estimate
                  </Button>
                  )}
                  {allowed(permissions, "create_invoices") && (
                  <Button
                    size="sm"
                    onClick={() => setDocFormJob({ job: selectedJob, type: "invoice" })}
                    className="text-xs h-9 rounded-lg border bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 border-blue-500/20"
                    data-testid="create-invoice-btn"
                  >
                    <Receipt className="w-3.5 h-3.5 mr-1.5" /> Create Invoice
                  </Button>
                  )}
                </div>
              )}
              <Button onClick={() => { setSelectedJob(null); setLinkedDocs([]); }} variant="ghost" className="text-muted-foreground hover:text-foreground rounded-xl sm:ml-auto" data-testid="close-detail-btn">Close</Button>
            </div>
          </div>
        </div>
      )}
      {/* Status Change Note Modal */}
      {statusNoteJob && (
        <StatusNoteModal
          job={statusNoteJob.job}
          status={statusNoteJob.status}
          onClose={() => setStatusNoteJob(null)}
          onSubmit={handleStatusNoteSubmit}
          submitting={submittingStatusNote}
        />
      )}

      {/* Completion Modal */}
      {completingJob && (
        <CompletionModal
          job={completingJob}
          onClose={() => setCompletingJob(null)}
          onSubmit={handleCompleteJob}
          submitting={submittingCompletion}
        />
      )}

      {/* Send Invoice Modal */}
      {invoicingJob && (
        <SendInvoiceModal
          job={invoicingJob}
          onClose={() => setInvoicingJob(null)}
          onSubmit={handleSendInvoice}
          submitting={submittingInvoice}
        />
      )}

      {/* Payment Method Picker */}
      {payDoc && (
        <PaymentMethodPicker
          doc={payDoc}
          onClose={() => setPayDoc(null)}
          submitting={docStatusUpdating === payDoc.invoice_id}
          onConfirm={async (method) => {
            await updateDocStatus(payDoc, "paid", { payment_method: method });
            setPayDoc(null);
          }}
        />
      )}

      {/* Tech Document Form Modal (Create Estimate / Create Invoice) */}
      {docFormJob && (
        <TechDocFormModal
          job={docFormJob.job}
          documentType={docFormJob.type}
          onClose={() => setDocFormJob(null)}
          onSubmit={handleCreateDoc}
          submitting={submittingDoc}
        />
      )}

      {/* Send For Signature Modal */}
      {sigDoc && (
        <SendForSignatureModal
          doc={sigDoc}
          isDark={isDark}
          onClose={() => setSigDoc(null)}
          onSent={() => { if (selectedJob) fetchLinkedDocs(selectedJob.job_id); }}
        />
      )}

      {/* Collect Payment Modal (Stripe card entry / payment link) */}
      {collectDoc && (
        <TechCollectPaymentModal
          doc={collectDoc}
          isDark={isDark}
          onClose={() => { setCollectDoc(null); if (selectedJob) fetchLinkedDocs(selectedJob.job_id); }}
          onPaid={() => { if (selectedJob) fetchLinkedDocs(selectedJob.job_id); }}
        />
      )}
    </div>
  );
};

export default TechJobs;
